import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Card, Input, EmptyState, useToast } from '../../design-system';
import DocumentsManager from './DocumentsManager';
import '../styles/Documents.css';

// A file-cabinet view of the firm's documents:
//   Level 1  Clients  — one folder per client (+ a pinned "Suspense" bucket for
//                       uploads that arrived without a client).
//   Level 2  Folders  — inside a client, one folder per category GROUP that has
//                       documents (KYC, Income Tax, GST, ROC, …), plus "All".
//   Level 3  Months   — inside a folder, one per month that its documents are
//                       dated to, plus "Undated". CONDITIONAL: see below.
//   Level 4  Documents — the DocumentsManager leaf: preview / download / move / edit.
// Suspense skips level 2 (its docs are unfiled) and goes straight to the leaf,
// where every row's primary action is "File it" → pick a client + folder.
//
// ⚠️ LEVEL 3 APPEARS ONLY WHERE IT HAS SOMETHING TO SAY. A document's date is
// optional, so a folder in which nothing is dated renders the leaf DIRECTLY —
// exactly as it did before this level existed. Making every folder grow a month
// step would charge every user an extra click to pay for a feature only some of
// them use. `monthInfo.months.length === 0` is the test, and it is why the leaf
// is reached by two different routes below.
//
// The current position lives in the URL query
// (?client=<id|suspense>&group=<gid>&month=<YYYY-MM|__undated__|__all__>), NOT
// component state — so the browser Back/Forward buttons walk up and down the
// folder hierarchy instead of leaving the Documents page.
const ALL = { id: '__all__', name: 'All documents', icon: '🗂️' };
// Matches UNDATED in src/utils/documentDate.js — the ?month= value for
// documents carrying no date of their own.
const UNDATED = '__undated__';

const groupIcon = {
  kyc: '🪪',
  registration: '📜',
  income_tax: '🧾',
  gst: '🧮',
  financials: '📊',
  roc: '🏛️',
  legal: '📝',
  dsc: '🔏',
  correspondence: '✉️',
  working: '🗒️',
  other: '📁',
};

function FolderCard({ icon, name, count, tone, onClick }) {
  return (
    <button type="button" className={`doc-folder ${tone ? `doc-folder-${tone}` : ''}`} onClick={onClick}>
      <span className="doc-folder-ico" aria-hidden>{icon}</span>
      <span className="doc-folder-name">{name}</span>
      <span className="doc-folder-count">{count === null ? '' : `${count} ${count === 1 ? 'doc' : 'docs'}`}</span>
    </button>
  );
}

function DocumentsBrowser() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Position is read from the URL — this is the single source of truth so that
  // Back/Forward (which change the URL) move between folder levels.
  const clientParam = searchParams.get('client'); // null | 'suspense' | <clientId>
  const groupParam = searchParams.get('group'); // null | <groupId> | '__all__'
  const monthParam = searchParams.get('month'); // null | 'YYYY-MM' | '__undated__' | '__all__'
  const inSuspense = clientParam === 'suspense';
  const inClient = !!clientParam && !inSuspense;
  // "All documents" spans every group, so there is no single folder to bucket by
  // month — it stays a flat list, as it was.
  const groupHasMonths = !!groupParam && groupParam !== ALL.id;

  // Navigation = push a new history entry (default for setSearchParams).
  const goRoot = () => setSearchParams({});
  const goSuspense = () => setSearchParams({ client: 'suspense' });
  const goClient = (id) => setSearchParams({ client: id });
  const goGroup = (gid) => setSearchParams({ client: clientParam, group: gid });
  const goMonth = (mk) => setSearchParams({ client: clientParam, group: groupParam, month: mk });

  const [allClients, setAllClients] = useState([]); // [{ clientId, name }]
  const [groupNames, setGroupNames] = useState({}); // { groupId: name }
  const [counts, setCounts] = useState(new Map()); // clientId(str) → count
  const [suspenseCount, setSuspenseCount] = useState(0);
  const [groups, setGroups] = useState([]); // level-2 folders for the active client
  // Level-3 month folders for the active group. `null` means NOT YET KNOWN —
  // distinct from a loaded `{ months: [] }`, which means "nothing in here is
  // dated, skip this level". Rendering the leaf on the strength of an unloaded
  // value would flash the flat list and then replace it with folders.
  const [monthInfo, setMonthInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpCounts = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Static lookups once — the full client list (for names + the level-1 grid) and
  // the category-group names (for breadcrumbs), independent of the current level.
  useEffect(() => {
    apiClient
      .get('/api/clients/options')
      .then((r) => setAllClients((r.data || []).map((c) => ({ clientId: c._id, name: c.displayName }))))
      .catch(() => {});
    apiClient
      .get('/api/documents/categories')
      .then((r) => setGroupNames(Object.fromEntries((r.data.groups || []).map((g) => [g.id, g.name]))))
      .catch(() => {});
  }, []);

  // Level 1 counts (per-client + suspense). Runs whenever we're at the root.
  useEffect(() => {
    if (clientParam) return undefined;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get('/api/documents/summary')
      .then((r) => {
        if (cancelled) return;
        setCounts(new Map((r.data.clients || []).map((c) => [String(c.clientId), c.count])));
        setSuspenseCount(r.data.suspenseCount || 0);
      })
      .catch(() => !cancelled && toast.danger('Failed to load documents'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // ⚠️ `toast` IS DELIBERATELY NOT A DEPENDENCY. A toast appearing is never a
    // reason to re-fetch, and listing it here made the failure path self-
    // sustaining: the request fails, this shows a toast, the toast re-renders
    // the provider, the effect re-runs, the request fails… The provider now
    // hands out a stable object (design-system/Toast.jsx), so this is belt and
    // braces — but a fetch keyed on a notification is the wrong shape either
    // way, and that is what made the loop possible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientParam, refreshKey]);

  // Level 2 — the active client's category folders.
  useEffect(() => {
    if (!inClient || groupParam) return undefined;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get('/api/documents/summary', { params: { clientId: clientParam } })
      .then((r) => !cancelled && setGroups(r.data.groups || []))
      .catch(() => !cancelled && toast.danger('Failed to load folders'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // Not a dependency, for the reason spelled out on the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inClient, clientParam, groupParam, refreshKey]);

  // Level 3 — the active folder's month buckets. Fetched even when we may end up
  // skipping the level, because "are any of these dated?" is the question that
  // decides whether the level exists at all.
  useEffect(() => {
    if (!inClient || !groupHasMonths) return undefined;
    let cancelled = false;
    setMonthInfo(null);
    apiClient
      .get('/api/documents/summary', { params: { clientId: clientParam, group: groupParam } })
      .then((r) => !cancelled && setMonthInfo({ months: r.data.months || [], undatedCount: r.data.undatedCount || 0 }))
      // On failure fall through to the flat list rather than stranding the user
      // on a spinner — the documents are the point, the folders are a convenience.
      .catch(() => !cancelled && setMonthInfo({ months: [], undatedCount: 0 }))
      .finally(() => {});
    return () => { cancelled = true; };
  }, [inClient, groupHasMonths, clientParam, groupParam, refreshKey]);

  const clientName = useMemo(
    () => allClients.find((c) => String(c.clientId) === String(clientParam))?.name || '…',
    [allClients, clientParam]
  );
  const groupName = groupParam === ALL.id ? ALL.name : groupNames[groupParam] || groupParam;
  // The month label comes from the summary where possible so the crumb reads the
  // same as the folder that was clicked; a direct URL hit falls back to
  // formatting the key itself.
  const monthName = useMemo(() => {
    if (!monthParam) return '';
    if (monthParam === ALL.id) return ALL.name;
    if (monthParam === UNDATED) return 'Undated';
    const known = monthInfo?.months.find((m) => m.key === monthParam);
    if (known) return known.label;
    const m = /^(\d{4})-(\d{2})$/.exec(monthParam);
    if (!m) return monthParam;
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
      .toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }, [monthParam, monthInfo]);

  const visibleClients = useMemo(() => {
    const withCounts = allClients
      .map((c) => ({ ...c, count: counts.get(String(c.clientId)) || 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const t = search.trim().toLowerCase();
    return t ? withCounts.filter((c) => c.name.toLowerCase().includes(t)) : withCounts;
  }, [allClients, counts, search]);

  const crumbs = [{ label: 'Documents', onClick: clientParam ? goRoot : null }];
  if (inSuspense) crumbs.push({ label: 'Suspense', onClick: null });
  if (inClient) {
    crumbs.push({ label: clientName, onClick: groupParam ? () => goClient(clientParam) : null });
    if (groupParam) {
      // The folder crumb is only a link back to the MONTH grid where such a grid
      // exists. In a folder with nothing dated, the folder IS the leaf, and a
      // link that navigates to the page you are already on is a dead control.
      crumbs.push({ label: groupName, onClick: monthParam ? () => goGroup(groupParam) : null });
      if (monthParam) crumbs.push({ label: monthName, onClick: null });
    }
  }

  const Breadcrumb = (
    <div className="doc-crumbs">
      {crumbs.map((c, i) => (
        <span key={i} className="doc-crumb">
          {c.onClick ? (
            <button type="button" className="doc-crumb-link" onClick={c.onClick}>{c.label}</button>
          ) : (
            <span className="doc-crumb-current">{c.label}</span>
          )}
          {i < crumbs.length - 1 && <span className="doc-crumb-sep" aria-hidden>›</span>}
        </span>
      ))}
    </div>
  );

  // ── Level 3: a folder's month buckets ───────────────────────────────────────
  // Only where the folder actually holds dated documents; otherwise this whole
  // branch is skipped and the leaf below renders instead.
  if (inClient && groupHasMonths && !monthParam) {
    if (monthInfo === null) {
      return <div>{Breadcrumb}<p className="doc-hint">Loading…</p></div>;
    }
    if (monthInfo.months.length > 0) {
      return (
        <div>
          {Breadcrumb}
          <div className="doc-folder-grid">
            <FolderCard icon={ALL.icon} name={ALL.name} count={null} onClick={() => goMonth(ALL.id)} />
            {monthInfo.months.map((m) => (
              <FolderCard key={m.key} icon="🗓️" name={m.label} count={m.count} onClick={() => goMonth(m.key)} />
            ))}
            {/* Only when something is actually undated — an empty "Undated"
                folder is a question the user never asked. */}
            {monthInfo.undatedCount > 0 && (
              <FolderCard
                icon="📄"
                name="Undated"
                count={monthInfo.undatedCount}
                tone="undated"
                onClick={() => goMonth(UNDATED)}
              />
            )}
          </div>
        </div>
      );
    }
    // Nothing here carries a date — fall through to the flat leaf.
  }

  // ── Leaf: the document list (a client folder, a month inside it, or Suspense) ─
  if (inSuspense || (inClient && groupParam)) {
    const monthFilter = monthParam && monthParam !== ALL.id ? monthParam : null;
    const subtitle = inSuspense
      ? 'Documents uploaded without a client — file each one to where it belongs'
      : [clientName, groupName, monthParam ? monthName : null].filter(Boolean).join(' · ');
    return (
      <div>
        {Breadcrumb}
        <Card>
          <Card.Header
            title={inSuspense ? 'Suspense' : monthFilter ? monthName : groupName}
            subtitle={subtitle}
          />
          <DocumentsManager
            key={inSuspense ? 'suspense' : `${clientParam}:${groupParam}:${monthParam || ''}`}
            clientId={inSuspense ? null : clientParam}
            suspenseMode={inSuspense}
            group={inSuspense || groupParam === ALL.id ? null : groupParam}
            month={monthFilter}
            embedded
            onChanged={bumpCounts}
          />
        </Card>
      </div>
    );
  }

  // ── Level 2: a client's category folders ────────────────────────────────────
  if (inClient) {
    const folders = [ALL, ...groups.map((g) => ({ id: g.id, name: g.name, count: g.count, icon: groupIcon[g.id] || '📁' }))];
    return (
      <div>
        {Breadcrumb}
        {loading ? (
          <p className="doc-hint">Loading…</p>
        ) : (
          <div className="doc-folder-grid">
            {folders.map((f) => (
              <FolderCard
                key={f.id}
                icon={f.icon || ALL.icon}
                name={f.name}
                count={f.id === ALL.id ? null : f.count}
                onClick={() => goGroup(f.id)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Level 1: clients + suspense ─────────────────────────────────────────────
  return (
    <div>
      {Breadcrumb}
      <div className="doc-browser-toolbar">
        <Input placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {loading ? (
        <p className="doc-hint">Loading…</p>
      ) : (
        <>
          {/* Suspense pinned first, styled distinctly. */}
          <div className="doc-folder-grid">
            <FolderCard icon="🗃️" name="Suspense" count={suspenseCount} tone="suspense" onClick={goSuspense} />
          </div>
          <div className="doc-section-label">Clients</div>
          {visibleClients.length === 0 ? (
            <EmptyState icon="👥" title="No clients" description="Add clients to start filing their documents." />
          ) : (
            <div className="doc-folder-grid">
              {visibleClients.map((c) => (
                <FolderCard key={c.clientId} icon="📂" name={c.name} count={c.count} onClick={() => goClient(c.clientId)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DocumentsBrowser;
